import { useEffect, useState } from "react";
import type { ReconnectCadenceState } from "@hub/shared";
import { Button, Modal, Select, toast } from "@/ui";
import { useEngineMutation, useEngineQuery } from "@/engine/hooks";
import { CADENCE_OPTIONS, MONTHS, QUALIFIERS, WEEKDAYS, describeDraft, draftFromState, setMode, toSaveInput, type CadenceDraft } from "./cadence";
import s from "../shared.module.css";

export function CadenceEditor({ open, onClose, contactId, state }: { open: boolean; onClose: () => void; contactId: string; state?: ReconnectCadenceState }) {
  const q = useEngineQuery("getReconnectCadence", { contactId }, { enabled: open && !state });
  const resolved = useEngineQuery("getResolvedReconnectCadence", { contactId }, { enabled: open });
  const base = state ?? q.data;
  const [draft, setDraft] = useState<CadenceDraft>(() => draftFromState(base ?? { state: "auto", row: null }));
  useEffect(() => {
    if (open && base) setDraft(draftFromState(base));
  }, [open, base]);
  const save = useEngineMutation("saveReconnectCadence", { invalidate: ["getContact", "getReconnectCadence", "getResolvedReconnectCadence", "getReconnectSuggestions", "getTimeline"] });

  const submit = async () => {
    await save.mutateAsync(toSaveInput(contactId, draft));
    toast("Cadence saved", "success");
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Reconnect cadence"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={save.isPending}>Save</Button>
        </>
      }
    >
      <div className="u-col" style={{ gap: 12 }}>
        <div className="u-small u-muted">
          {resolved.data ? `Currently ${resolved.data.state === "disabled" ? "off" : resolved.data.state} (${resolved.data.source === "auto" ? "automatic" : `from ${resolved.data.source}`})${resolved.data.nextDue ? `, next around ${new Date(resolved.data.nextDue).toLocaleDateString()}` : ""}.` : ""}
        </div>
        <div className={s.cardGrid} style={{ gridTemplateColumns: "repeat(3, 1fr)" }} role="radiogroup" aria-label="Cadence">
          {CADENCE_OPTIONS.map((o) => (
            <button key={o.id} type="button" role="radio" aria-checked={draft.mode === o.id} className={s.themeCard} data-active={draft.mode === o.id ? "true" : undefined} onClick={() => setDraft(setMode(draft, o.id))} style={{ textAlign: "left" }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{o.label}</div>
              <div className="u-muted" style={{ fontSize: 11, marginTop: 2 }}>{o.hint}</div>
            </button>
          ))}
        </div>
        {(draft.mode === "weekly" || draft.mode === "monthly") && (
          <div className="u-row" style={{ gap: 8, flexWrap: "wrap" }}>
            {draft.mode === "monthly" && draft.weekday !== null && (
              <Select value={draft.qualifier ?? "first"} onChange={(e) => setDraft({ ...draft, qualifier: e.target.value as CadenceDraft["qualifier"] })} aria-label="Which week" style={{ width: 120 }}>
                {QUALIFIERS.map((q) => <option key={q.id} value={q.id}>{q.label}</option>)}
              </Select>
            )}
            <Select value={draft.weekday ?? ""} onChange={(e) => setDraft({ ...draft, weekday: e.target.value === "" ? null : Number(e.target.value), monthDay: e.target.value === "" ? draft.monthDay : null })} aria-label="Weekday" style={{ width: 160 }}>
              <option value="">Any day</option>
              {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
            </Select>
            {draft.mode === "monthly" && draft.weekday === null && (
              <Select value={draft.monthDay ?? ""} onChange={(e) => setDraft({ ...draft, monthDay: e.target.value === "" ? null : Number(e.target.value) })} aria-label="Day of month" style={{ width: 140 }}>
                <option value="">Any date</option>
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>Day {d}</option>)}
              </Select>
            )}
          </div>
        )}
        {draft.mode === "yearly" && (
          <div className="u-row" style={{ gap: 8 }}>
            <Select value={draft.month ?? ""} onChange={(e) => setDraft({ ...draft, month: e.target.value === "" ? null : Number(e.target.value) })} aria-label="Month" style={{ width: 160 }}>
              <option value="">Any month</option>
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </Select>
            <Select value={draft.monthDay ?? ""} onChange={(e) => setDraft({ ...draft, monthDay: e.target.value === "" ? null : Number(e.target.value) })} aria-label="Day" style={{ width: 120 }} disabled={draft.month === null}>
              <option value="">Any day</option>
              {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
            </Select>
          </div>
        )}
        <div data-testid="cadence-summary" style={{ padding: "8px 10px", background: "var(--bg-sunken)", borderRadius: 6, fontSize: 13 }}>{describeDraft(draft)}</div>
      </div>
    </Modal>
  );
}
